import { useQuery } from '@tanstack/react-query';
import { Truck, Package, Inbox, Clock, DollarSign } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

/**
 * Mirrors the `/admin/fulfillment/analytics` handler
 * (functions/src/routes/admin/fulfillment.ts → GET /analytics). Every numeric
 * field is already coerced with parseInt/parseFloat server-side, so these are
 * real numbers (not postgres.js strings).
 */
interface FulfillmentAnalytics {
  period: string;
  total_shipments: number;
  total_cost: number;
  avg_cost: number;
  carrier_breakdown: Array<{
    carrier: string;
    count: number | string;
    total_cost: number | string;
  }>;
  avg_hours_to_label: number;
  avg_hours_to_ship: number;
  avg_days_to_deliver: number;
  queue_count: number;
}

export default function FulfillmentSummaryWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'fulfillment', 'analytics', '30d'],
    queryFn: () => api.get<FulfillmentAnalytics>('/admin/fulfillment/analytics?period=30d'),
    staleTime: 60000,
  });

  return (
    <WidgetShell
      title="Fulfillment"
      icon={<Truck />}
      to="/admin/fulfillment-dashboard"
      isLoading={isLoading}
      error={error}
      isEmpty={!!data && data.total_shipments === 0 && data.queue_count === 0}
      emptyIcon={<Truck />}
      emptyMessage="No shipments in the last 30 days."
      skeletonClassName="h-40"
    >
      {data && (
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            title="Shipments (30d)"
            value={data.total_shipments.toLocaleString()}
            icon={<Package />}
            tone="blue"
          />
          <StatCard
            title="In queue"
            value={data.queue_count.toLocaleString()}
            icon={<Inbox />}
            tone="amber"
            subtitle="Awaiting fulfillment"
          />
          <StatCard
            title="Avg days to deliver"
            value={data.avg_days_to_deliver.toFixed(1)}
            icon={<Clock />}
            tone="green"
          />
          <StatCard
            title="Avg label cost"
            value={formatCurrency(data.avg_cost)}
            icon={<DollarSign />}
            tone="purple"
          />
        </div>
      )}
    </WidgetShell>
  );
}
